/**
 * Position color palette — auto-assigns a consistent brand color per pozisyon
 * so UI chips, badges and strip rows carry the same hue across the app.
 *
 * Call Center is pinned to amber because that's the universal "rule-driven %7"
 * flag the operators already associate with. Other positions get a deterministic
 * color derived from the pozisyon string hash so the SAME pozisyon always gets
 * the SAME color across the Prim table, totals strip, personnel cards, etc.
 */

const _norm = (p) => {
  // Lowercase (handles Turkish 'İ'→'i̇'), strip combining marks (NFD → remove diacritics),
  // then convert Turkish-specific chars to ASCII equivalents so comparisons are robust
  // regardless of how the string is stored (e.g. 'ARAŞTIRMA' vs 'araştırma' vs 'ARASTIRMA').
  const base = String(p || "").trim().toLowerCase();
  const stripped = base.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  return stripped
    .replace(/ı/g, "i")
    .replace(/ş/g, "s")
    .replace(/ğ/g, "g")
    .replace(/ü/g, "u")
    .replace(/ö/g, "o")
    .replace(/ç/g, "c");
};

// Core palette — soft, high-contrast on white, avoids red (reserved for errors).
// Each entry: { chipBg, chipBorder, chipText, dotBg, dotText }
const _PALETTE = [
  { chipBg: "#EFF6FF", chipBorder: "#93C5FD", chipText: "#1E40AF", dotBg: "#3B82F6", dotText: "#FFFFFF" }, // blue
  { chipBg: "#ECFDF5", chipBorder: "#86EFAC", chipText: "#065F46", dotBg: "#10B981", dotText: "#FFFFFF" }, // emerald
  { chipBg: "#F5F3FF", chipBorder: "#C4B5FD", chipText: "#5B21B6", dotBg: "#8B5CF6", dotText: "#FFFFFF" }, // violet
  { chipBg: "#FFF1F2", chipBorder: "#FDA4AF", chipText: "#9F1239", dotBg: "#F43F5E", dotText: "#FFFFFF" }, // rose
  { chipBg: "#F0FDFA", chipBorder: "#5EEAD4", chipText: "#115E59", dotBg: "#14B8A6", dotText: "#FFFFFF" }, // teal
  { chipBg: "#FEFCE8", chipBorder: "#FDE047", chipText: "#854D0E", dotBg: "#EAB308", dotText: "#FFFFFF" }, // yellow
  { chipBg: "#F5F5F4", chipBorder: "#A8A29E", chipText: "#44403C", dotBg: "#78716C", dotText: "#FFFFFF" }, // stone
  { chipBg: "#EFF6FF", chipBorder: "#60A5FA", chipText: "#1D4ED8", dotBg: "#2563EB", dotText: "#FFFFFF" }, // indigo
  { chipBg: "#FDF2F8", chipBorder: "#F9A8D4", chipText: "#9D174D", dotBg: "#EC4899", dotText: "#FFFFFF" }, // pink
  { chipBg: "#FFF7ED", chipBorder: "#FDBA74", chipText: "#9A3412", dotBg: "#F97316", dotText: "#FFFFFF" }, // orange
];

// Pinned colors (special-cased)
const _CC_COLOR = {
  chipBg: "#FEF3C7", chipBorder: "#FCD34D", chipText: "#92400E",
  dotBg: "#F59E0B", dotText: "#FFFFFF",
};  // amber
const _ARASTIRMA_COLOR = {
  chipBg: "#EEF2FF", chipBorder: "#A5B4FC", chipText: "#3730A3",
  dotBg: "#6366F1", dotText: "#FFFFFF",
};  // indigo

function _hash(s) {
  // djb2
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

export function isCallCenterPozisyon(pozisyon) {
  const n = _norm(pozisyon);
  return n === "call center" || n === "cagri merkezi" || n === "callcenter";
}

export function isArastirmaPozisyon(pozisyon) {
  const n = _norm(pozisyon);
  return n === "arastirma";
}

export function isHacizBirimiPozisyon(pozisyon) {
  const n = _norm(pozisyon);
  // Covers "Haciz Birimi", "Haciz" alone (any casing/diacritics variants)
  return n === "haciz birimi" || n === "haciz" || n === "haciz birim";
}

// =========================================================================
// Custom color overrides (admin-defined per pozisyon)
// =========================================================================
// Admin can set a custom hex color per pozisyon via /position-templates/colors.
// We cache the map in localStorage so getPozisyonColor stays synchronous for
// every render site across the app. The cache is refreshed on app bootstrap
// and after any mutation in PositionTemplateManager.

const _CUSTOM_CACHE = { map: {}, loaded: false };
const _CUSTOM_STORAGE_KEY = "bordromat_pozisyon_colors";

// Hydrate from localStorage synchronously on module load so very first render
// already has the previously-fetched map available.
try {
  if (typeof window !== "undefined" && window.localStorage) {
    const raw = window.localStorage.getItem(_CUSTOM_STORAGE_KEY);
    if (raw) _CUSTOM_CACHE.map = JSON.parse(raw) || {};
  }
} catch (_) { /* ignore quota / parse errors */ }

/** Replace the full custom color map (called by PozisyonColorsProvider). */
export function setCustomPozisyonColors(colorsByIsim) {
  _CUSTOM_CACHE.map = { ...(colorsByIsim || {}) };
  _CUSTOM_CACHE.loaded = true;
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      window.localStorage.setItem(_CUSTOM_STORAGE_KEY, JSON.stringify(_CUSTOM_CACHE.map));
    }
  } catch (_) {}
  // Notify any mounted palette consumer to re-render
  try {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event("bordromat:pozisyon-colors-updated"));
    }
  } catch (_) {}
}

function _customColorFor(pozisyon) {
  const map = _CUSTOM_CACHE.map || {};
  // Exact match
  if (map[pozisyon]) return map[pozisyon];
  // Case/diacritic-insensitive fallback
  const nTarget = _norm(pozisyon);
  for (const k of Object.keys(map)) {
    if (_norm(k) === nTarget) return map[k];
  }
  return null;
}

// Convert a hex "#RRGGBB" into a {chipBg, chipBorder, chipText, dotBg, dotText}
// palette that visually matches the custom color.
function _hexToPalette(hex) {
  const h = String(hex || "").trim();
  const m = /^#([0-9a-fA-F]{6})$/.exec(h);
  if (!m) return null;
  const r = parseInt(m[1].slice(0, 2), 16);
  const g = parseInt(m[1].slice(2, 4), 16);
  const b = parseInt(m[1].slice(4, 6), 16);
  const bg = `rgba(${r}, ${g}, ${b}, 0.12)`;
  const border = `rgba(${r}, ${g}, ${b}, 0.5)`;
  // Perceived luminance → pick white or dark text
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  const dotText = lum < 0.6 ? "#FFFFFF" : "#1E293B";
  // Darker chipText derived from the hex (shift towards black for readability)
  const dr = Math.max(0, Math.round(r * 0.4));
  const dg = Math.max(0, Math.round(g * 0.4));
  const db = Math.max(0, Math.round(b * 0.4));
  const chipText = `rgb(${dr}, ${dg}, ${db})`;
  return {
    chipBg: bg,
    chipBorder: border,
    chipText,
    dotBg: hex,
    dotText,
  };
}

/**
 * Return the color scheme for a given pozisyon string.
 * Priority: admin custom color → pinned specials → deterministic hash.
 */
export function getPozisyonColor(pozisyon) {
  if (!pozisyon) {
    return { chipBg: "#F8FAFC", chipBorder: "#E2E8F0", chipText: "#64748B", dotBg: "#CBD5E1", dotText: "#1E293B" };
  }
  const custom = _customColorFor(pozisyon);
  if (custom) {
    const pal = _hexToPalette(custom);
    if (pal) return pal;
  }
  if (isCallCenterPozisyon(pozisyon)) return _CC_COLOR;
  if (isArastirmaPozisyon(pozisyon)) return _ARASTIRMA_COLOR;
  const n = _norm(pozisyon);
  return _PALETTE[_hash(n) % _PALETTE.length];
}
